import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Card, CardHeader, CardTitle, CardBody } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { useAuth, setCreatingStaff } from '../contexts/AuthContext';
import { createStaff, StaffData } from '../firebase/auth';
import { collection, getDocs, query, orderBy, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import toast from 'react-hot-toast';
import { FiPlus, FiUser, FiAtSign, FiPhoneCall, FiBriefcase, FiShield, FiEye, FiEdit, FiSearch, FiArrowUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Table } from '../components/ui/Table';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
`;


const Badge = styled.span<{ variant?: 'staff' | 'supervisor' | 'manager' | 'owner' | 'active' | 'inactive' }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ variant = 'staff' }) => {
    switch (variant) {
      case 'supervisor':
        return `${theme.colors.primary[500]}20`;
      case 'manager':
        return `${theme.colors.accent.success}20`;
      case 'owner':
        return `${theme.colors.primary[600]}20`;
      case 'active':
        return `${theme.colors.accent.success}20`;
      case 'inactive':
        return `${theme.colors.accent.danger}20`;
      default:
        return `${theme.colors.light.background.tertiary}`;
    }
  }};
  color: ${({ variant = 'staff' }) => {
    switch (variant) {
      case 'supervisor':
        return theme.colors.primary[600];
      case 'manager':
        return theme.colors.accent.success;
      case 'owner':
        return theme.colors.primary[700];
      case 'active':
        return theme.colors.accent.success;
      case 'inactive':
        return theme.colors.accent.danger;
      default:
        return theme.colors.light.text.secondary;
    }
  }};
`;

const StatusText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const StatusReason = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.muted};
  font-style: italic;
  margin-top: ${theme.spacing[1]};
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${theme.spacing[4]};
  opacity: 0.5;
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  margin: 0;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  background-color: transparent;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    color: ${theme.colors.primary[600]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Switch = styled.label<{ checked: boolean }>`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ checked }) =>
    checked ? theme.colors.accent.success : theme.colors.light.border.medium};
    transition: 0.3s;
    border-radius: 24px;

    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
      transform: ${({ checked }) => checked ? 'translateX(20px)' : 'translateX(0)'};
    }
  }
`;

const StatusReasonInput = styled.input`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  margin-top: ${theme.spacing[2]};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing[3]} 0;
  border-bottom: 1px solid ${theme.colors.light.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.secondary};
`;

const DetailValue = styled.span`
  color: ${theme.colors.light.text.primary};
`;

interface StaffFormData {
  email: string;
  password: string;
  displayName: string;
  phone: string;
  department: string;
  role: 'staff' | 'supervisor' | 'manager' | 'owner';
  access: {
    staff: boolean;
    dashboard: boolean;
    guards: boolean;
    clients: boolean;
    deployment: boolean;
    attendance: boolean;
    finance: boolean;
    inventory: boolean;
    transaction: boolean;
    invoice: boolean;
    expense: boolean;
    invoiceManagement: boolean;
    accountReceivable: boolean;
    profitLoss: boolean;
    pettyCash: boolean;
    expenseManagement: boolean;
  };
  permissions: {
    staff: 'view' | 'add' | 'edit' | 'delete' | null;
    dashboard: 'view' | 'edit' | null;
    guards: 'view' | 'add' | 'edit' | 'delete' | null;
    clients: 'view' | 'add' | 'edit' | 'delete' | null;
    deployment: 'view' | 'add' | 'edit' | 'delete' | null;
    attendance: 'view' | 'add' | 'edit' | 'delete' | null;
    finance: 'view' | 'add' | 'edit' | 'delete' | null;
    inventory: 'view' | 'add' | 'edit' | 'delete' | null;
    transaction: 'view' | 'add' | 'edit' | 'delete' | null;
    invoice: 'view' | 'add' | 'edit' | 'delete' | null;
    expense: 'view' | 'add' | 'edit' | 'delete' | null;
    invoiceManagement: 'view' | 'add' | 'edit' | 'delete' | null;
    accountReceivable: 'view' | 'add' | 'edit' | 'delete' | null;
    profitLoss: 'view' | 'add' | 'edit' | 'delete' | null;
    pettyCash: 'view' | 'add' | 'edit' | 'delete' | null;
    expenseManagement: 'view' | 'add' | 'edit' | 'delete' | null;
  };
  isActive: boolean;
  inactiveReason?: string;
}

// Mobile-specific styled components
const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  @media (max-width: ${theme.breakpoints.md}) {
    overflow: visible;
    height: auto;
    min-height: auto;
    display: block;
  }
`;

const MobileHeaderContainer = styled.div`
  display: none;
  @media (max-width: ${theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing[3]};
    position: sticky;
    top: 0;
    width: calc(100% + ${theme.spacing[4]} * 2);
    margin-left: -${theme.spacing[4]};
    margin-right: -${theme.spacing[4]};
    background-color: ${theme.colors.light.background.primary};
    z-index: 40;
    padding: ${theme.spacing[4]};
    margin-bottom: ${theme.spacing[4]};
    border-bottom: 1px solid ${theme.colors.light.border.light};
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  }
`;

const SearchInputContainer = styled.div`
  flex: 1;
  min-width: 250px;
  display: flex;
  align-items: stretch;
`;

const MobileActionRow = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  width: 100%;
  align-items: center;
`;

const MobileCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  padding-bottom: ${theme.spacing[4]};
`;

const MobileCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  overflow: hidden;
  padding: ${theme.spacing[2]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  cursor: pointer;
  transition: background-color 0.2s;
  min-height: 80px;

  &:active {
    background-color: ${theme.colors.light.background.secondary};
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const MobileCardImage = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${theme.colors.primary[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary[600]};
  font-weight: bold;
  flex-shrink: 0;
  border: 2px solid ${theme.colors.primary[100]};
`;

const MobileCardHeaderContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MobileCardTitle = styled.div`
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  white-space: normal;
`;

const MobileCardSubtitle = styled.div`
  color: ${theme.colors.light.text.secondary};
  font-size: ${theme.typography.fontSize.xs};
`;

const MobileCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  padding-left: calc(48px + ${theme.spacing[3]}); /* Align with content */
`;

const ScrollToTopButton = styled.button`
  position: fixed;
  bottom: ${theme.spacing[6]};
  right: ${theme.spacing[6]};
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${theme.colors.primary[500]};
  color: ${theme.colors.white};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme.shadows.lg};
  transition: all 0.3s ease;
  z-index: 50;

  &:hover {
    background-color: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${theme.breakpoints.md}) {
    bottom: ${theme.spacing[4]};
    right: ${theme.spacing[4]};
    width: 44px;
    height: 44px;
  }
`;

// Helper function to get initials from name
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};


// Staff Table Row Component
interface StaffTableRowProps {
  staff: StaffData;
  canAddStaff: boolean;
  canViewStatus: boolean;
  updatingStatus: string | null;
  onViewDetails: (staff: StaffData) => void;
  onStatusChange: (staff: StaffData, isActive: boolean, reason?: string) => void;
}

const StaffTableRow: React.FC<StaffTableRowProps> = ({
  staff,
  canAddStaff,
  canViewStatus,
  updatingStatus,
  onViewDetails,
  onStatusChange,
}) => {
  const isActive = (staff as any).isActive !== false;
  const [statusReason, setStatusReason] = React.useState((staff as any).inactiveReason || '');
  const [showReasonInput, setShowReasonInput] = React.useState(false);

  return (
    <Table.Row>
      <Table.Cell>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
          <FiUser size={16} style={{ color: theme.colors.light.text.secondary }} />
          {staff.displayName}
        </div>
      </Table.Cell>
      <Table.Cell>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
          <FiAtSign size={16} style={{ color: theme.colors.light.text.secondary }} />
          {staff.email}
        </div>
      </Table.Cell>
      <Table.Cell>
        {staff.phone ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
            <FiPhoneCall size={16} style={{ color: theme.colors.light.text.secondary }} />
            {staff.phone}
          </div>
        ) : (
          <span style={{ color: theme.colors.light.text.muted }}>—</span>
        )}
      </Table.Cell>
      <Table.Cell>
        {staff.department || (staff.departments && staff.departments[0]) ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
            <FiBriefcase size={16} style={{ color: theme.colors.light.text.secondary }} />
            {staff.department || staff.departments?.[0] || '—'}
          </div>
        ) : (
          <span style={{ color: theme.colors.light.text.muted }}>—</span>
        )}
      </Table.Cell>
      <Table.Cell>
        <Badge variant={staff.role}>
          {staff.role === 'owner' && <FiShield size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
          {staff.role === 'staff' ? 'Staff' :
            staff.role === 'supervisor' ? 'Supervisor' :
              staff.role === 'manager' ? 'Manager' :
                staff.role === 'owner' ? 'Owner' :
                  String(staff.role).charAt(0).toUpperCase() + String(staff.role).slice(1)}
        </Badge>
      </Table.Cell>
      {canViewStatus && (
        <Table.Cell>
          {canAddStaff ? (
            <SwitchContainer>
              <Switch checked={isActive}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => {
                    const newStatus = e.target.checked;
                    if (!newStatus) {
                      setShowReasonInput(true);
                    } else {
                      onStatusChange(staff, true);
                      setShowReasonInput(false);
                    }
                  }}
                  disabled={updatingStatus === staff.uid}
                />
                <span className="slider" />
              </Switch>
              {showReasonInput && !isActive && (
                <div style={{ flex: 1, marginLeft: theme.spacing[2] }}>
                  <StatusReasonInput
                    type="text"
                    placeholder="Reason for deactivation"
                    value={statusReason}
                    onChange={(e) => setStatusReason(e.target.value)}
                    onBlur={() => {
                      if (statusReason.trim()) {
                        onStatusChange(staff, false, statusReason);
                      }
                      setShowReasonInput(false);
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && statusReason.trim()) {
                        onStatusChange(staff, false, statusReason);
                        setShowReasonInput(false);
                      }
                    }}
                  />
                </div>
              )}
            </SwitchContainer>
          ) : (
            <StatusText>
              <Badge variant={isActive ? 'active' : 'inactive'}>
                {isActive ? 'Active' : 'Inactive'}
              </Badge>
              {!isActive && (staff as any).inactiveReason && (
                <StatusReason>Reason: {(staff as any).inactiveReason}</StatusReason>
              )}
            </StatusText>
          )}
        </Table.Cell>
      )}
      <Table.Cell>
        <ActionButton
          onClick={() => onViewDetails(staff)}
          title="View Details"
        >
          <FiEye size={18} />
        </ActionButton>
      </Table.Cell>
    </Table.Row>
  );
};

const StaffPage: React.FC = () => {
  const { companyData, user, staffData } = useAuth();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<StaffData | null>(null);
  const [staffList, setStaffList] = useState<StaffData[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [formData, setFormData] = useState<StaffFormData>({
    email: '',
    password: '',
    displayName: '',
    phone: '',
    department: '',
    role: 'staff',
    access: {
      staff: false,
      dashboard: true, // Dashboard access is automatic for all staff
      guards: false,
      clients: false,
      deployment: false,
      attendance: false,
      finance: false,
      inventory: false,
      transaction: false,
      invoice: false,
      expense: false,
      invoiceManagement: false,
      accountReceivable: false,
      profitLoss: false,
      pettyCash: false,
      expenseManagement: false,
    },
    permissions: {
      staff: null,
      dashboard: 'view', // Default to view
      guards: null,
      clients: null,
      deployment: null,
      attendance: null,
      finance: null,
      inventory: null,
      transaction: null,
      invoice: null,
      expense: null,
      invoiceManagement: null,
      accountReceivable: null,
      profitLoss: null,
      pettyCash: null,
      expenseManagement: null,
    },
    isActive: true,
    inactiveReason: '',
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [searchTerm, setSearchTerm] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredStaff = staffList.filter(staff =>
    staff.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (staff.role && staff.role.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (staff.department && staff.department.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Common departments - can be moved to company data later
  const departments = [
    'Security',
    'HR',
    'Operations',
    'Management',
    'IT',
    'Finance',
    'Administration',
  ];

  useEffect(() => {
    if (!companyData?.id) return;
    fetchStaff();
  }, [companyData]);

  // Check if user is a company owner/admin (logged in from companies, not staff)
  // Company owners have full access regardless of staff permissions
  // A user is a company owner if:
  // 1. companyData exists AND
  // 2. Either staffData doesn't exist (pure company owner) OR
  // 3. Their email matches the company email (company owner who might also be in staff)
  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData || // No staffData = pure company owner
    (userEmail && (
      companyData.email?.toLowerCase() === userEmail ||
      companyData.emails?.some((email: string) => email.toLowerCase() === userEmail)
    )) // Email matches company = company owner
  );

  // Check if current user has permission to add staff
  // If user is company owner/admin, they have ALL power - always show add button
  // If user is staff, check their permissions
  const isStaffUser = !!staffData && !isCompanyOwner;

  // Company owners always have full access - show add button
  const canAddStaff = isCompanyOwner
    ? true // Company owners always have full access - ALL POWER
    : isStaffUser
      ? (staffData?.access?.staff === true && (staffData?.permissions?.staff === 'edit' || staffData?.permissions?.staff === 'add' || staffData?.permissions?.staff === 'delete'))
      : !!companyData; // Fallback: if companyData exists but not detected as owner, still allow

  // Check if user can edit staff (same as add permission)
  const canEditStaff = isCompanyOwner
    ? true // Company owners always have full access
    : canAddStaff;

  // Check if user can view status (has staff access)
  const canViewStatus = isCompanyOwner
    ? true // Company owners always have full access
    : isStaffUser
      ? (staffData?.access?.staff === true)
      : !!companyData;

  // Helper function to get the company ID
  // This ensures staff is always added to the correct company
  const getCompanyId = async (): Promise<string | null> => {
    // First, try to use companyData.id if available
    if (companyData?.id) {
      return companyData.id;
    }

    // If companyData is not set but staffData exists, find the company
    // by searching for the staff member in all companies
    if (staffData && user) {
      try {
        const companiesRef = collection(db, 'companies');
        const allCompanies = await getDocs(companiesRef);

        for (const companyDoc of allCompanies.docs) {
          const staffDocRef = doc(db, 'companies', companyDoc.id, 'staff', user.uid);
          const staffDoc = await getDoc(staffDocRef);
          if (staffDoc.exists()) {
            return companyDoc.id;
          }
        }
      } catch (err) {
        console.error('Error finding company for staff:', err);
      }
    }

    return null;
  };

  const fetchStaff = async () => {
    const targetCompanyId = await getCompanyId();
    if (!targetCompanyId) return;

    try {
      const staffRef = collection(db, 'companies', targetCompanyId, 'staff');
      const q = query(staffRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const staff: StaffData[] = [];
      querySnapshot.forEach((doc) => {
        staff.push(doc.data() as StaffData);
      });
      setStaffList(staff);
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Failed to fetch staff');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // If role is changed to "owner", automatically set all access to view
    if (name === 'role' && value === 'owner') {
      setFormData((prev) => ({
        ...prev,
        role: value as 'staff' | 'supervisor' | 'manager' | 'owner',
        access: {
          staff: true,
          dashboard: true,
          guards: true,
          clients: true,
          deployment: true,
          attendance: true,
          finance: true,
          inventory: true,
          transaction: true,
          invoice: true,
          expense: true,
          invoiceManagement: true,
          accountReceivable: true,
          profitLoss: true,
          pettyCash: true,
          expenseManagement: true,
        },
        permissions: {
          staff: 'view',
          dashboard: 'view',
          guards: 'view',
          clients: 'view',
          deployment: 'view',
          attendance: 'view',
          finance: 'view',
          inventory: 'view',
          transaction: 'view',
          invoice: 'view',
          expense: 'view',
          invoiceManagement: 'view',
          accountReceivable: 'view',
          profitLoss: 'view',
          pettyCash: 'view',
          expenseManagement: 'view',
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAccessChange = (key: 'staff' | 'dashboard' | 'guards' | 'clients' | 'deployment' | 'attendance' | 'finance' | 'inventory' | 'transaction' | 'invoice' | 'expense' | 'invoiceManagement' | 'accountReceivable' | 'profitLoss' | 'pettyCash' | 'expenseManagement', checked: boolean) => {
    setFormData((prev) => {
      // If role is owner, always set permissions to view
      const isOwner = prev.role === 'owner';

      // Special logic for finance: when finance is checked, transaction is checked by default
      if (key === 'finance' && checked && !isOwner) {
        return {
          ...prev,
          access: {
            ...prev.access,
            finance: true,
            transaction: true, // Transaction is default when finance is enabled
          },
          permissions: {
            ...prev.permissions,
            finance: 'view',
            transaction: 'view', // Set transaction permission when finance is enabled
          },
        };
      }

      // If finance is checked and owner, set all finance-related permissions
      if (key === 'finance' && checked && isOwner) {
        return {
          ...prev,
          access: {
            ...prev.access,
            finance: true,
            transaction: true,
            invoice: true,
            expense: true,
            invoiceManagement: true,
            accountReceivable: true,
            profitLoss: true,
            pettyCash: true,
            expenseManagement: true,
          },
          permissions: {
            ...prev.permissions,
            finance: 'view',
            transaction: 'view',
            invoice: 'view',
            expense: 'view',
            invoiceManagement: 'view',
            accountReceivable: 'view',
            profitLoss: 'view',
            pettyCash: 'view',
            expenseManagement: 'view',
          },
        };
      }

      // If finance is unchecked, uncheck all finance-related items
      if (key === 'finance' && !checked && !isOwner) {
        return {
          ...prev,
          access: {
            ...prev.access,
            finance: false,
            transaction: false,
            invoice: false,
            expense: false,
            invoiceManagement: false,
            accountReceivable: false,
            profitLoss: false,
            pettyCash: false,
            expenseManagement: false,
          },
          permissions: {
            ...prev.permissions,
            finance: null,
            transaction: null,
            invoice: null,
            expense: null,
            invoiceManagement: null,
            accountReceivable: null,
            profitLoss: null,
            pettyCash: null,
            expenseManagement: null,
          },
        };
      }

      return {
        ...prev,
        access: {
          ...prev.access,
          [key]: checked || isOwner, // Owner always has access
        },
        permissions: {
          ...prev.permissions,
          [key]: (checked || isOwner) ? (isOwner ? 'view' : 'view') : null,
        },
      };
    });
  };

  const handlePermissionChange = (key: 'staff' | 'dashboard' | 'guards' | 'clients' | 'deployment' | 'attendance' | 'finance' | 'inventory' | 'transaction' | 'invoice' | 'expense' | 'invoiceManagement' | 'accountReceivable' | 'profitLoss' | 'pettyCash' | 'expenseManagement', value: 'view' | 'edit' | null) => {
    setFormData((prev) => {
      // If role is owner, always keep permissions as 'view'
      if (prev.role === 'owner') {
        return {
          ...prev,
          permissions: {
            ...prev.permissions,
            [key]: 'view',
          },
        };
      }
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [key]: value,
        },
      };
    });
  };

  const handleViewDetails = (staff: StaffData) => {
    setSelectedStaff(staff);
    setIsDetailsModalOpen(true);
  };

  const handleEditStaff = (staff: StaffData) => {
    setSelectedStaff(staff);
    setFormData({
      email: staff.email,
      password: '', // Don't pre-fill password
      displayName: staff.displayName,
      phone: staff.phone || '',
      department: staff.department || '',
      role: staff.role,
      access: {
        staff: staff.access?.staff || false,
        dashboard: staff.access?.dashboard || true,
        guards: staff.access?.guards || false,
        clients: staff.access?.clients || false,
        deployment: staff.access?.deployment || false,
        attendance: staff.access?.attendance || false,
        finance: staff.access?.finance || false,
        inventory: staff.access?.inventory || false,
        transaction: staff.access?.transaction || false,
        invoice: staff.access?.invoice || false,
        expense: staff.access?.expense || false,
        invoiceManagement: staff.access?.invoiceManagement || false,
        accountReceivable: staff.access?.accountReceivable || false,
        profitLoss: staff.access?.profitLoss || false,
        pettyCash: staff.access?.pettyCash || false,
        expenseManagement: staff.access?.expenseManagement || false,
      },
      permissions: {
        staff: (staff.permissions?.staff === 'view' || staff.permissions?.staff === 'edit' || staff.permissions?.staff === 'add' || staff.permissions?.staff === 'delete')
          ? (staff.permissions.staff === 'add' || staff.permissions.staff === 'delete' ? 'edit' : staff.permissions.staff)
          : null,
        dashboard: staff.permissions?.dashboard || 'view',
        guards: (staff.permissions?.guards === 'view' || staff.permissions?.guards === 'edit' || staff.permissions?.guards === 'add' || staff.permissions?.guards === 'delete')
          ? (staff.permissions.guards === 'add' || staff.permissions.guards === 'delete' ? 'edit' : staff.permissions.guards)
          : null,
        clients: (staff.permissions?.clients === 'view' || staff.permissions?.clients === 'edit' || staff.permissions?.clients === 'add' || staff.permissions?.clients === 'delete')
          ? (staff.permissions.clients === 'add' || staff.permissions.clients === 'delete' ? 'edit' : staff.permissions.clients)
          : null,
        deployment: (staff.permissions?.deployment === 'view' || staff.permissions?.deployment === 'edit' || staff.permissions?.deployment === 'add' || staff.permissions?.deployment === 'delete')
          ? (staff.permissions.deployment === 'add' || staff.permissions.deployment === 'delete' ? 'edit' : staff.permissions.deployment)
          : null,
        attendance: (staff.permissions?.attendance === 'view' || staff.permissions?.attendance === 'edit' || staff.permissions?.attendance === 'add' || staff.permissions?.attendance === 'delete')
          ? (staff.permissions.attendance === 'add' || staff.permissions.attendance === 'delete' ? 'edit' : staff.permissions.attendance)
          : null,
        finance: (staff.permissions?.finance === 'view' || staff.permissions?.finance === 'edit' || staff.permissions?.finance === 'add' || staff.permissions?.finance === 'delete')
          ? (staff.permissions.finance === 'add' || staff.permissions.finance === 'delete' ? 'edit' : staff.permissions.finance)
          : null,
        inventory: (staff.permissions?.inventory === 'view' || staff.permissions?.inventory === 'edit' || staff.permissions?.inventory === 'add' || staff.permissions?.inventory === 'delete')
          ? (staff.permissions.inventory === 'add' || staff.permissions.inventory === 'delete' ? 'edit' : staff.permissions.inventory)
          : null,
        transaction: (staff.permissions?.transaction === 'view' || staff.permissions?.transaction === 'edit' || staff.permissions?.transaction === 'add' || staff.permissions?.transaction === 'delete')
          ? (staff.permissions.transaction === 'add' || staff.permissions.transaction === 'delete' ? 'edit' : staff.permissions.transaction)
          : null,
        invoice: (staff.permissions?.invoice === 'view' || staff.permissions?.invoice === 'edit' || staff.permissions?.invoice === 'add' || staff.permissions?.invoice === 'delete')
          ? (staff.permissions.invoice === 'add' || staff.permissions.invoice === 'delete' ? 'edit' : staff.permissions.invoice)
          : null,
        expense: (staff.permissions?.expense === 'view' || staff.permissions?.expense === 'edit' || staff.permissions?.expense === 'add' || staff.permissions?.expense === 'delete')
          ? (staff.permissions.expense === 'add' || staff.permissions.expense === 'delete' ? 'edit' : staff.permissions.expense)
          : null,
        invoiceManagement: (staff.permissions?.invoiceManagement === 'view' || staff.permissions?.invoiceManagement === 'edit' || staff.permissions?.invoiceManagement === 'add' || staff.permissions?.invoiceManagement === 'delete')
          ? (staff.permissions.invoiceManagement === 'add' || staff.permissions.invoiceManagement === 'delete' ? 'edit' : staff.permissions.invoiceManagement)
          : null,
        accountReceivable: (staff.permissions?.accountReceivable === 'view' || staff.permissions?.accountReceivable === 'edit' || staff.permissions?.accountReceivable === 'add' || staff.permissions?.accountReceivable === 'delete')
          ? (staff.permissions.accountReceivable === 'add' || staff.permissions.accountReceivable === 'delete' ? 'edit' : staff.permissions.accountReceivable)
          : null,
        profitLoss: (staff.permissions?.profitLoss === 'view' || staff.permissions?.profitLoss === 'edit' || staff.permissions?.profitLoss === 'add' || staff.permissions?.profitLoss === 'delete')
          ? (staff.permissions.profitLoss === 'add' || staff.permissions.profitLoss === 'delete' ? 'edit' : staff.permissions.profitLoss)
          : null,
        pettyCash: (staff.permissions?.pettyCash === 'view' || staff.permissions?.pettyCash === 'edit' || staff.permissions?.pettyCash === 'add' || staff.permissions?.pettyCash === 'delete')
          ? (staff.permissions.pettyCash === 'add' || staff.permissions.pettyCash === 'delete' ? 'edit' : staff.permissions.pettyCash)
          : null,
        expenseManagement: (staff.permissions?.expenseManagement === 'view' || staff.permissions?.expenseManagement === 'edit' || staff.permissions?.expenseManagement === 'add' || staff.permissions?.expenseManagement === 'delete')
          ? (staff.permissions.expenseManagement === 'add' || staff.permissions.expenseManagement === 'delete' ? 'edit' : staff.permissions.expenseManagement)
          : null,
      },
      isActive: staff.isActive !== false,
      inactiveReason: staff.inactiveReason || '',
    });
    setIsDetailsModalOpen(false);
    setIsEditModalOpen(true);
  };

  const handleStatusChange = async (staff: StaffData, isActive: boolean, reason?: string) => {
    const targetCompanyId = await getCompanyId();
    if (!targetCompanyId) {
      toast.error('Company data not found');
      return;
    }

    setUpdatingStatus(staff.uid);
    try {
      const staffRef = doc(db, 'companies', targetCompanyId, 'staff', staff.uid);
      await updateDoc(staffRef, {
        isActive,
        inactiveReason: isActive ? null : (reason || ''),
        statusUpdatedAt: new Date(),
        statusUpdatedBy: user?.uid || '',
      });

      toast.success(`Staff member ${isActive ? 'activated' : 'deactivated'} successfully`);
      await fetchStaff();
    } catch (error) {
      console.error('Error updating staff status:', error);
      toast.error('Failed to update staff status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!user) {
      toast.error('User information not found');
      return;
    }

    // Get the company ID - this ensures staff is added to the correct company
    const targetCompanyId = await getCompanyId();
    if (!targetCompanyId) {
      toast.error('Company data not found. Please ensure you are logged in with a valid company account.');
      return;
    }

    setLoading(true);
    try {
      // Dashboard access is always true for all staff, permission defaults to 'view'
      const staffPermissions: {
        staff?: 'view' | 'add' | 'edit' | 'delete' | null;
        dashboard?: 'view' | 'edit' | null;
        [key: string]: 'view' | 'add' | 'edit' | 'delete' | null | undefined;
      } = {
        ...formData.permissions,
        dashboard: formData.permissions.dashboard || ('view' as 'view'),
      };
      const staffAccess = {
        ...formData.access,
        dashboard: true, // Always true for all staff
      };

      // If editing (selectedStaff exists), update instead of create
      if (selectedStaff && isEditModalOpen) {
        const staffRef = doc(db, 'companies', targetCompanyId, 'staff', selectedStaff.uid);
        await updateDoc(staffRef, {
          displayName: formData.displayName,
          phone: formData.phone || null,
          department: formData.department || null,
          departments: formData.department ? [formData.department] : null,
          role: formData.role,
          permissions: staffPermissions,
          access: staffAccess,
          updatedAt: new Date(),
          updatedBy: user.uid,
        });

        toast.success('Staff member updated successfully!');
        setIsEditModalOpen(false);
        setSelectedStaff(null);
      } else {
        // Creating new staff
        if (!formData.password) {
          toast.error('Password is required for new staff');
          setLoading(false);
          return;
        }

        try {
          // Get admin credentials from sessionStorage to restore session after creating staff
          const adminEmail = sessionStorage.getItem('admin_email_temp') || user?.email;
          const adminPassword = sessionStorage.getItem('admin_password_temp');

          if (!adminEmail) {
            toast.error('Admin email not found. Please log out and log back in.');
            setLoading(false);
            return;
          }

          if (!adminPassword) {
            toast.error('Admin password not found. Please log out and log back in, then try creating staff again.');
            setLoading(false);
            return;
          }

          // Set flag to prevent AuthContext from clearing company data during staff creation
          setCreatingStaff(true, companyData);

          console.log('Creating staff with companyId:', targetCompanyId);
          console.log('Admin email for restore:', adminEmail);

          const createdStaff = await createStaff(
            formData.email,
            formData.password,
            formData.displayName,
            targetCompanyId,
            formData.phone || undefined,
            formData.department || undefined,
            formData.department ? [formData.department] : undefined,
            formData.role,
            staffPermissions,
            staffAccess,
            formData.isActive,
            formData.inactiveReason || undefined,
            adminEmail,
            adminPassword
          );

          console.log('Staff created successfully:', createdStaff);

          // Wait a moment for auth state to fully stabilize and AuthContext to update
          await new Promise(resolve => setTimeout(resolve, 500));

          // Verify admin is still logged in by checking auth.currentUser directly
          // (The hook's user state might not have updated yet)
          const currentAuthUser = auth.currentUser;
          if (!currentAuthUser || currentAuthUser.email?.toLowerCase() !== adminEmail?.toLowerCase()) {
            console.error('❌ Admin session lost after staff creation!');
            console.error('Expected admin email:', adminEmail);
            console.error('Current auth user:', currentAuthUser?.email);
            toast.error('Staff created but session was lost. Please refresh the page.');
            setTimeout(() => {
              window.location.reload();
            }, 1000);
            return;
          }

          console.log('✅ Admin session verified - still logged in as', currentAuthUser.email);
          toast.success('Staff member created successfully!');
          setIsModalOpen(false);

          // Reset form
          setFormData({
            email: '',
            password: '',
            displayName: '',
            phone: '',
            department: '',
            role: 'staff',
            access: {
              staff: false,
              dashboard: true,
              guards: false,
              clients: false,
              deployment: false,
              attendance: false,
              finance: false,
              inventory: false,
              transaction: false,
              invoice: false,
              expense: false,
              invoiceManagement: false,
              accountReceivable: false,
              profitLoss: false,
              pettyCash: false,
              expenseManagement: false,
            },
            permissions: {
              staff: null,
              dashboard: 'view',
              guards: null,
              clients: null,
              deployment: null,
              attendance: null,
              finance: null,
              inventory: null,
              transaction: null,
              invoice: null,
              expense: null,
              invoiceManagement: null,
              accountReceivable: null,
              profitLoss: null,
              pettyCash: null,
              expenseManagement: null,
            },
            isActive: true,
            inactiveReason: '',
          });

          // Refresh the staff list to show the new staff member
          await fetchStaff();

          // Clear the flag after staff creation is complete
          setCreatingStaff(false, null);
        } catch (error: any) {
          console.error('Error creating staff:', error);
          toast.error(error.message || 'Failed to create staff member');

          // Clear the flag even on error
          setCreatingStaff(false, null);
        }
      }

      // Refresh staff list
      await fetchStaff();
    } catch (error: any) {
      console.error('Error saving staff:', error);
      toast.error(error.message || 'Failed to save staff member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <PageContainer>
        {isMobile ? (
          <>
            <MobileHeaderContainer>
              <SearchInputContainer>
                <div style={{ position: 'relative', width: '100vw' }}>
                  <Input
                    placeholder="Search staff..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    dark
                    fullWidth
                  />
                  <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: theme.colors.light.text.muted }}>
                    <FiSearch size={20} />
                  </div>
                </div>
              </SearchInputContainer>

              <MobileActionRow>
                {canAddStaff && (
                  <Button
                    onClick={() => setIsModalOpen(true)}
                    fullWidth
                    style={{ justifyContent: 'center', height: '48px', fontSize: theme.typography.fontSize.base }}
                  >
                    <FiPlus size={20} style={{ marginRight: theme.spacing[2] }} />
                    Add Staff
                  </Button>
                )}
              </MobileActionRow>
            </MobileHeaderContainer>

            <MobileCardContainer>
              {filteredStaff.length === 0 ? (
                <EmptyState>
                  <EmptyStateText>No staff members found.</EmptyStateText>
                </EmptyState>
              ) : (
                filteredStaff.map((staff) => (
                  <MobileCard key={staff.uid} onClick={() => handleViewDetails(staff)}>
                    <MobileCardHeader>
                      <MobileCardHeaderContent>
                        <MobileCardImage>{getInitials(staff.displayName || 'Unknown')}</MobileCardImage>
                        <div>
                          <MobileCardTitle>{staff.displayName}</MobileCardTitle>
                          <MobileCardSubtitle>{staff.role ? (staff.role.charAt(0).toUpperCase() + staff.role.slice(1)) : 'Staff'} • {staff.department || 'No Dept'}</MobileCardSubtitle>
                        </div>
                      </MobileCardHeaderContent>
                      <Badge variant={staff.isActive !== false ? 'active' : 'inactive'}>
                        {staff.isActive !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </MobileCardHeader>
                    <MobileCardBody>
                      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2], fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                        <FiAtSign size={14} />
                        {staff.email}
                      </div>
                      {staff.phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2], fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                          <FiPhoneCall size={14} />
                          {staff.phone}
                        </div>
                      )}
                    </MobileCardBody>
                  </MobileCard>
                ))
              )}
            </MobileCardContainer>
          </>
        ) : (
          <>
            <PageHeader>
              <PageTitle>Staff Information</PageTitle>
              {canAddStaff && (
                <Button onClick={() => {
                  setFormData(prev => ({ ...prev, email: '', password: '', displayName: '', phone: '', department: '' }));
                  setIsModalOpen(true);
                }}>
                  <FiPlus size={18} />
                  Add Staff
                </Button>
              )}
            </PageHeader>

            <Card>
              <CardHeader>
                <CardTitle>Staff Members</CardTitle>
              </CardHeader>
              <CardBody>
                {staffList.length === 0 ? (
                  <EmptyState>
                    <EmptyStateIcon>👥</EmptyStateIcon>
                    <EmptyStateText>No staff members yet. Add your first staff member to get started.</EmptyStateText>
                  </EmptyState>
                ) : (
                  <Table>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Name</Table.HeaderCell>
                        <Table.HeaderCell>Email</Table.HeaderCell>
                        <Table.HeaderCell>Phone</Table.HeaderCell>
                        <Table.HeaderCell>Department</Table.HeaderCell>
                        <Table.HeaderCell>Role</Table.HeaderCell>
                        {canViewStatus && <Table.HeaderCell>Status</Table.HeaderCell>}
                        <Table.HeaderCell>Actions</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {staffList.map((staff) => (
                        <StaffTableRow
                          key={staff.uid}
                          staff={staff}
                          canAddStaff={canAddStaff}
                          canViewStatus={canViewStatus}
                          updatingStatus={updatingStatus}
                          onViewDetails={handleViewDetails}
                          onStatusChange={handleStatusChange}
                        />
                      ))}
                    </Table.Body>
                  </Table>
                )}
              </CardBody>
            </Card>
          </>
        )}

        {showScrollTop && (
          <ScrollToTopButton onClick={scrollToTop}>
            <FiArrowUp size={24} />
          </ScrollToTopButton>
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Add New Staff Member"
          footer={
            <>
              <Button variant="secondary" onClick={() => setIsModalOpen(false)} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={() => handleSubmit()} loading={loading} type="submit">
                Create Staff
              </Button>
            </>
          }
        >
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Input
                label="Full Name"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                required
                fullWidth
                dark
                placeholder="John Doe"
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  dark
                  placeholder="john.doe@company.com"
                />
              </FormGroup>
              <FormGroup>
                <Input
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  dark
                  placeholder="••••••••"
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Input
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  fullWidth
                  dark
                  placeholder="+1234567890"
                />
              </FormGroup>
              <FormGroup>
                <label style={{
                  display: 'block',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[1]
                }}>
                  Department
                </label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: theme.spacing[3],
                    border: `1px solid ${theme.colors.light.border.medium}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: theme.typography.fontSize.base,
                    color: theme.colors.light.text.primary,
                    backgroundColor: theme.colors.white,
                  }}
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <label style={{
                display: 'block',
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.light.text.primary,
                marginBottom: theme.spacing[1]
              }}>
                Role
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: theme.spacing[3],
                  border: `1px solid ${theme.colors.light.border.medium}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: theme.typography.fontSize.base,
                  color: theme.colors.light.text.primary,
                  backgroundColor: theme.colors.white,
                }}
              >
                <option value="staff">Staff</option>
                <option value="supervisor">Supervisor</option>
                <option value="manager">Manager</option>
                <option value="owner">Owner</option>
              </select>
            </FormGroup>

            <FormGroup>
              <label style={{
                display: 'block',
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.light.text.primary,
                marginBottom: theme.spacing[3],
                marginTop: theme.spacing[4],
              }}>
                Access & Permissions
                {formData.role === 'owner' && (
                  <span style={{
                    fontSize: theme.typography.fontSize.xs,
                    color: theme.colors.primary[600],
                    marginLeft: theme.spacing[2],
                    fontWeight: theme.typography.fontWeight.normal,
                  }}>
                    (Owner has view access to all)
                  </span>
                )}
              </label>

              {/* Staff Access */}
              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.staff}
                    onChange={(e) => handleAccessChange('staff', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: '18px',
                      height: '18px',
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Staff Information
                </label>
                {(formData.role === 'owner' || formData.access.staff) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.staff || 'view')}
                      onChange={(e) => handlePermissionChange('staff', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.medium}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Manage Guards Access */}
              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.guards}
                    onChange={(e) => handleAccessChange('guards', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: '18px',
                      height: '18px',
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Manage Guards
                </label>
                {(formData.role === 'owner' || formData.access.guards) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.guards || 'view')}
                      onChange={(e) => handlePermissionChange('guards', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.medium}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Manage Clients Access */}
              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.clients}
                    onChange={(e) => handleAccessChange('clients', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: '18px',
                      height: '18px',
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Manage Clients
                </label>
                {(formData.role === 'owner' || formData.access.clients) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.clients || 'view')}
                      onChange={(e) => handlePermissionChange('clients', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.medium}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Deployment Access */}
              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.deployment}
                    onChange={(e) => handleAccessChange('deployment', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: '18px',
                      height: '18px',
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Deployment
                </label>
                {(formData.role === 'owner' || formData.access.deployment) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.deployment || 'view')}
                      onChange={(e) => handlePermissionChange('deployment', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.medium}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Attendance Access */}
              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.attendance}
                    onChange={(e) => handleAccessChange('attendance', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: '18px',
                      height: '18px',
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Attendance
                </label>
                {(formData.role === 'owner' || formData.access.attendance) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.attendance || 'view')}
                      onChange={(e) => handlePermissionChange('attendance', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.medium}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Finance Management Access */}
              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.finance}
                    onChange={(e) => handleAccessChange('finance', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: '18px',
                      height: '18px',
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Finance Management
                </label>
                {(formData.role === 'owner' || formData.access.finance) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.finance || 'view')}
                      onChange={(e) => handlePermissionChange('finance', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.medium}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Inventory Access */}
              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.inventory}
                    onChange={(e) => handleAccessChange('inventory', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: '18px',
                      height: '18px',
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Inventory
                </label>
                {(formData.role === 'owner' || formData.access.inventory) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.inventory || 'view')}
                      onChange={(e) => handlePermissionChange('inventory', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.medium}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Transaction Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.transaction}
                      onChange={(e) => handleAccessChange('transaction', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: '18px',
                        height: '18px',
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Transaction (Default)
                  </label>
                  {formData.access.transaction && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.transaction || 'view')}
                        onChange={(e) => handlePermissionChange('transaction', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.medium}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Invoice Revenue Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.invoice}
                      onChange={(e) => handleAccessChange('invoice', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: '18px',
                        height: '18px',
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Invoice Revenue
                  </label>
                  {formData.access.invoice && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.invoice || 'view')}
                        onChange={(e) => handlePermissionChange('invoice', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.medium}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Expense Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.expense}
                      onChange={(e) => handleAccessChange('expense', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: '18px',
                        height: '18px',
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Expense
                  </label>
                  {formData.access.expense && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.expense || 'view')}
                        onChange={(e) => handlePermissionChange('expense', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.medium}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Invoice Management Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.invoiceManagement}
                      onChange={(e) => handleAccessChange('invoiceManagement', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: '18px',
                        height: '18px',
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Invoice Management
                  </label>
                  {formData.access.invoiceManagement && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.invoiceManagement || 'view')}
                        onChange={(e) => handlePermissionChange('invoiceManagement', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.medium}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Account Receivable Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.accountReceivable}
                      onChange={(e) => handleAccessChange('accountReceivable', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: '18px',
                        height: '18px',
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Account Receivable
                  </label>
                  {formData.access.accountReceivable && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.accountReceivable || 'view')}
                        onChange={(e) => handlePermissionChange('accountReceivable', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.medium}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Profit & Loss Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.profitLoss}
                      onChange={(e) => handleAccessChange('profitLoss', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: '18px',
                        height: '18px',
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Profit & Loss
                  </label>
                  {formData.access.profitLoss && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.profitLoss || 'view')}
                        onChange={(e) => handlePermissionChange('profitLoss', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.medium}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Petty Cash Book Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.pettyCash}
                      onChange={(e) => handleAccessChange('pettyCash', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: '18px',
                        height: '18px',
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Petty Cash Book
                  </label>
                  {formData.access.pettyCash && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.pettyCash || 'view')}
                        onChange={(e) => handlePermissionChange('pettyCash', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.medium}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Expense Management Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.expenseManagement}
                      onChange={(e) => handleAccessChange('expenseManagement', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: '18px',
                        height: '18px',
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Expense Management
                  </label>
                  {formData.access.expenseManagement && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.expenseManagement || 'view')}
                        onChange={(e) => handlePermissionChange('expenseManagement', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.medium}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

            </FormGroup>
          </form>
        </Modal>

        {/* Staff Details Modal */}
        <Modal
          isOpen={isDetailsModalOpen}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedStaff(null);
          }}
          title="Staff Details"
          footer={
            <>
              <Button variant="secondary" onClick={() => setIsDetailsModalOpen(false)}>
                Close
              </Button>
              {canEditStaff && selectedStaff && (
                <Button onClick={() => handleEditStaff(selectedStaff)}>
                  <FiEdit size={18} style={{ marginRight: theme.spacing[2] }} />
                  Edit
                </Button>
              )}
            </>
          }
        >
          {selectedStaff && (
            <div>
              <DetailRow>
                <DetailLabel>Name:</DetailLabel>
                <DetailValue>{selectedStaff.displayName}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Email:</DetailLabel>
                <DetailValue>{selectedStaff.email}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Phone:</DetailLabel>
                <DetailValue>{selectedStaff.phone || '—'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Department:</DetailLabel>
                <DetailValue>{selectedStaff.department || selectedStaff.departments?.[0] || '—'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Role:</DetailLabel>
                <DetailValue>
                  <Badge variant={selectedStaff.role}>
                    {(selectedStaff.role === 'supervisor' || selectedStaff.role === 'owner') && <FiShield size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
                    {selectedStaff.role.charAt(0).toUpperCase() + selectedStaff.role.slice(1)}
                  </Badge>
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Status:</DetailLabel>
                <DetailValue>
                  <Badge variant={(selectedStaff as any).isActive === false ? 'staff' : 'manager'}>
                    {(selectedStaff as any).isActive === false ? 'Inactive' : 'Active'}
                  </Badge>
                </DetailValue>
              </DetailRow>
              {(selectedStaff as any).inactiveReason && (
                <DetailRow>
                  <DetailLabel>Inactive Reason:</DetailLabel>
                  <DetailValue>{(selectedStaff as any).inactiveReason}</DetailValue>
                </DetailRow>
              )}
              <DetailRow>
                <DetailLabel>Staff Access:</DetailLabel>
                <DetailValue>{selectedStaff.access?.staff ? 'Yes' : 'No'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Staff Permission:</DetailLabel>
                <DetailValue>{selectedStaff.permissions?.staff || 'None'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Dashboard Access:</DetailLabel>
                <DetailValue>{selectedStaff.access?.dashboard ? 'Yes' : 'No'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Dashboard Permission:</DetailLabel>
                <DetailValue>{selectedStaff.permissions?.dashboard || 'None'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Guards Access:</DetailLabel>
                <DetailValue>{selectedStaff.access?.guards ? 'Yes' : 'No'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Guards Permission:</DetailLabel>
                <DetailValue>{selectedStaff.permissions?.guards || 'None'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Clients Access:</DetailLabel>
                <DetailValue>{selectedStaff.access?.clients ? 'Yes' : 'No'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Clients Permission:</DetailLabel>
                <DetailValue>{selectedStaff.permissions?.clients || 'None'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Finance Access:</DetailLabel>
                <DetailValue>{selectedStaff.access?.finance ? 'Yes' : 'No'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Finance Permission:</DetailLabel>
                <DetailValue>{selectedStaff.permissions?.finance || 'None'}</DetailValue>
              </DetailRow>
              {selectedStaff.access?.finance && (
                <>
                  <DetailRow style={{ marginLeft: theme.spacing[4] }}>
                    <DetailLabel>Transaction Access:</DetailLabel>
                    <DetailValue>{selectedStaff.access?.transaction ? 'Yes' : 'No'}</DetailValue>
                  </DetailRow>
                  <DetailRow style={{ marginLeft: theme.spacing[4] }}>
                    <DetailLabel>Invoice Access:</DetailLabel>
                    <DetailValue>{selectedStaff.access?.invoice ? 'Yes' : 'No'}</DetailValue>
                  </DetailRow>
                  <DetailRow style={{ marginLeft: theme.spacing[4] }}>
                    <DetailLabel>Expense Access:</DetailLabel>
                    <DetailValue>{selectedStaff.access?.expense ? 'Yes' : 'No'}</DetailValue>
                  </DetailRow>
                  <DetailRow style={{ marginLeft: theme.spacing[4] }}>
                    <DetailLabel>Invoice Management Access:</DetailLabel>
                    <DetailValue>{selectedStaff.access?.invoiceManagement ? 'Yes' : 'No'}</DetailValue>
                  </DetailRow>
                  <DetailRow style={{ marginLeft: theme.spacing[4] }}>
                    <DetailLabel>Account Receivable Access:</DetailLabel>
                    <DetailValue>{selectedStaff.access?.accountReceivable ? 'Yes' : 'No'}</DetailValue>
                  </DetailRow>
                  <DetailRow style={{ marginLeft: theme.spacing[4] }}>
                    <DetailLabel>Profit & Loss Access:</DetailLabel>
                    <DetailValue>{selectedStaff.access?.profitLoss ? 'Yes' : 'No'}</DetailValue>
                  </DetailRow>
                  <DetailRow style={{ marginLeft: theme.spacing[4] }}>
                    <DetailLabel>Petty Cash Book Access:</DetailLabel>
                    <DetailValue>{selectedStaff.access?.pettyCash ? 'Yes' : 'No'}</DetailValue>
                  </DetailRow>
                  <DetailRow style={{ marginLeft: theme.spacing[4] }}>
                    <DetailLabel>Expense Management Access:</DetailLabel>
                    <DetailValue>{selectedStaff.access?.expenseManagement ? 'Yes' : 'No'}</DetailValue>
                  </DetailRow>
                </>
              )}
              {selectedStaff.createdAt && (
                <DetailRow>
                  <DetailLabel>Created At:</DetailLabel>
                  <DetailValue>
                    {selectedStaff.createdAt instanceof Date
                      ? selectedStaff.createdAt.toLocaleDateString()
                      : (selectedStaff.createdAt as any).toDate
                        ? (selectedStaff.createdAt as any).toDate().toLocaleDateString()
                        : '—'}
                  </DetailValue>
                </DetailRow>
              )}
            </div>
          )}
        </Modal>

        {/* Edit Staff Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedStaff(null);
          }}
          title="Edit Staff Member"
          footer={
            <>
              <Button variant="secondary" onClick={() => setIsEditModalOpen(false)} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={() => handleSubmit()} loading={loading} type="submit">
                Update Staff
              </Button>
            </>
          }
        >
          <form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Input
                  label="Full Name"
                  name="displayName"
                  type="text"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  dark
                  placeholder="John Doe"
                />
              </FormGroup>
              <FormGroup>
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  dark
                  placeholder="john@example.com"
                  disabled
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Input
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  fullWidth
                  dark
                  placeholder="+1234567890"
                />
              </FormGroup>
              <FormGroup>
                <label style={{
                  display: 'block',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[1],
                }}>
                  Department
                </label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: theme.spacing[3],
                    border: `1px solid ${theme.colors.light.border.light}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: theme.typography.fontSize.sm,
                    backgroundColor: theme.colors.white,
                    color: theme.colors.light.text.primary,
                  }}
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <label style={{
                display: 'block',
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.light.text.primary,
                marginBottom: theme.spacing[1],
              }}>
                Role
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: theme.spacing[3],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: theme.typography.fontSize.sm,
                  backgroundColor: theme.colors.white,
                  color: theme.colors.light.text.primary,
                }}
              >
                <option value="staff">Staff</option>
                <option value="supervisor">Supervisor</option>
                <option value="manager">Manager</option>
                <option value="owner">Owner</option>
              </select>
            </FormGroup>

            <FormGroup>
              <label style={{
                display: 'block',
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.light.text.primary,
                marginBottom: theme.spacing[2],
              }}>
                Access & Permissions
                {formData.role === 'owner' && (
                  <span style={{
                    fontSize: theme.typography.fontSize.xs,
                    color: theme.colors.primary[600],
                    marginLeft: theme.spacing[2],
                    fontWeight: theme.typography.fontWeight.normal,
                  }}>
                    (Owner has view access to all)
                  </span>
                )}
              </label>

              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[3],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.staff}
                    onChange={(e) => handleAccessChange('staff', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: 18,
                      height: 18,
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Staff Information
                </label>
                {(formData.role === 'owner' || formData.access.staff) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.staff || 'view')}
                      onChange={(e) => handlePermissionChange('staff', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.light}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Manage Guards Access */}
              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.guards}
                    onChange={(e) => handleAccessChange('guards', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: 18,
                      height: 18,
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Manage Guards
                </label>
                {(formData.role === 'owner' || formData.access.guards) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.guards || 'view')}
                      onChange={(e) => handlePermissionChange('guards', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.light}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Manage Clients Access */}
              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.clients}
                    onChange={(e) => handleAccessChange('clients', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: 18,
                      height: 18,
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Manage Clients
                </label>
                {(formData.role === 'owner' || formData.access.clients) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.clients || 'view')}
                      onChange={(e) => handlePermissionChange('clients', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.light}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Deployment Access */}
              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.deployment}
                    onChange={(e) => handleAccessChange('deployment', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: 18,
                      height: 18,
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Deployment
                </label>
                {(formData.role === 'owner' || formData.access.deployment) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.deployment || 'view')}
                      onChange={(e) => handlePermissionChange('deployment', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.light}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              <div style={{
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'block',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[1],
                }}>
                  Dashboard (All staff have access)
                </label>
                <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                  <label style={{
                    display: 'block',
                    fontSize: theme.typography.fontSize.xs,
                    color: theme.colors.light.text.secondary,
                    marginBottom: theme.spacing[1],
                  }}>
                    Permission Level:
                  </label>
                  <select
                    value={formData.role === 'owner' ? 'view' : (formData.permissions.dashboard || 'view')}
                    onChange={(e) => handlePermissionChange('dashboard', e.target.value as 'view' | 'edit' | null)}
                    disabled={formData.role === 'owner'}
                    style={{
                      width: '100%',
                      padding: theme.spacing[2],
                      border: `1px solid ${theme.colors.light.border.light}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: theme.typography.fontSize.sm,
                      backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                      color: theme.colors.light.text.primary,
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <option value="view">View Only</option>
                    <option value="edit">View & Edit</option>
                  </select>
                </div>
              </div>

              {/* Attendance Access */}
              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.attendance}
                    onChange={(e) => handleAccessChange('attendance', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: 18,
                      height: 18,
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Attendance
                </label>
                {(formData.role === 'owner' || formData.access.attendance) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.attendance || 'view')}
                      onChange={(e) => handlePermissionChange('attendance', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.light}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Finance Management Access */}
              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.finance}
                    onChange={(e) => handleAccessChange('finance', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: 18,
                      height: 18,
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Finance Management
                </label>
                {(formData.role === 'owner' || formData.access.finance) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.finance || 'view')}
                      onChange={(e) => handlePermissionChange('finance', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.light}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Inventory Access */}
              <div style={{
                marginBottom: theme.spacing[4],
                padding: theme.spacing[4],
                border: `1px solid ${theme.colors.light.border.light}`,
                borderRadius: theme.borderRadius.md,
                opacity: formData.role === 'owner' ? 0.6 : 1,
                pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2],
                  cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.role === 'owner' ? true : formData.access.inventory}
                    onChange={(e) => handleAccessChange('inventory', e.target.checked)}
                    disabled={formData.role === 'owner'}
                    style={{
                      marginRight: theme.spacing[2],
                      width: 18,
                      height: 18,
                      cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                    }}
                  />
                  Inventory
                </label>
                {(formData.role === 'owner' || formData.access.inventory) && (
                  <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                    <label style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.light.text.secondary,
                      marginBottom: theme.spacing[1],
                    }}>
                      Permission Level:
                    </label>
                    <select
                      value={formData.role === 'owner' ? 'view' : (formData.permissions.inventory || 'view')}
                      onChange={(e) => handlePermissionChange('inventory', e.target.value as 'view' | 'edit' | null)}
                      disabled={formData.role === 'owner'}
                      style={{
                        width: '100%',
                        padding: theme.spacing[2],
                        border: `1px solid ${theme.colors.light.border.light}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                        backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                        color: theme.colors.light.text.primary,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="view">View Only</option>
                      <option value="edit">View, Edit & Create</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Transaction Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.transaction}
                      onChange={(e) => handleAccessChange('transaction', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: 18,
                        height: 18,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Transaction (Default)
                  </label>
                  {formData.access.transaction && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.transaction || 'view')}
                        onChange={(e) => handlePermissionChange('transaction', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.light}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Invoice Revenue Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.invoice}
                      onChange={(e) => handleAccessChange('invoice', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: 18,
                        height: 18,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Invoice Revenue
                  </label>
                  {formData.access.invoice && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.invoice || 'view')}
                        onChange={(e) => handlePermissionChange('invoice', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.light}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Expense Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.expense}
                      onChange={(e) => handleAccessChange('expense', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: 18,
                        height: 18,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Expense
                  </label>
                  {formData.access.expense && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.expense || 'view')}
                        onChange={(e) => handlePermissionChange('expense', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.light}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Invoice Management Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.invoiceManagement}
                      onChange={(e) => handleAccessChange('invoiceManagement', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: 18,
                        height: 18,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Invoice Management
                  </label>
                  {formData.access.invoiceManagement && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.invoiceManagement || 'view')}
                        onChange={(e) => handlePermissionChange('invoiceManagement', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.light}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Account Receivable Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.accountReceivable}
                      onChange={(e) => handleAccessChange('accountReceivable', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: 18,
                        height: 18,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Account Receivable
                  </label>
                  {formData.access.accountReceivable && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.accountReceivable || 'view')}
                        onChange={(e) => handlePermissionChange('accountReceivable', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.light}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Profit & Loss Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.profitLoss}
                      onChange={(e) => handleAccessChange('profitLoss', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: 18,
                        height: 18,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Profit & Loss
                  </label>
                  {formData.access.profitLoss && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.profitLoss || 'view')}
                        onChange={(e) => handlePermissionChange('profitLoss', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.light}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Petty Cash Book Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.pettyCash}
                      onChange={(e) => handleAccessChange('pettyCash', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: 18,
                        height: 18,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Petty Cash Book
                  </label>
                  {formData.access.pettyCash && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.pettyCash || 'view')}
                        onChange={(e) => handlePermissionChange('pettyCash', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.light}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Expense Management Access - shown when Finance is enabled */}
              {formData.access.finance && (
                <div style={{
                  marginBottom: theme.spacing[4],
                  padding: theme.spacing[4],
                  border: `1px solid ${theme.colors.light.border.light}`,
                  borderRadius: theme.borderRadius.md,
                  marginLeft: theme.spacing[6],
                  opacity: formData.role === 'owner' ? 0.6 : 1,
                  pointerEvents: formData.role === 'owner' ? 'none' : 'auto',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.light.text.primary,
                    marginBottom: theme.spacing[2],
                    cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.role === 'owner' ? true : formData.access.expenseManagement}
                      onChange={(e) => handleAccessChange('expenseManagement', e.target.checked)}
                      disabled={formData.role === 'owner'}
                      style={{
                        marginRight: theme.spacing[2],
                        width: 18,
                        height: 18,
                        cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Expense Management
                  </label>
                  {formData.access.expenseManagement && (
                    <div style={{ marginLeft: theme.spacing[6], marginTop: theme.spacing[2] }}>
                      <label style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.light.text.secondary,
                        marginBottom: theme.spacing[1],
                      }}>
                        Permission Level:
                      </label>
                      <select
                        value={formData.role === 'owner' ? 'view' : (formData.permissions.expenseManagement || 'view')}
                        onChange={(e) => handlePermissionChange('expenseManagement', e.target.value as 'view' | 'edit' | null)}
                        disabled={formData.role === 'owner'}
                        style={{
                          width: '100%',
                          padding: theme.spacing[2],
                          border: `1px solid ${theme.colors.light.border.light}`,
                          borderRadius: theme.borderRadius.md,
                          fontSize: theme.typography.fontSize.sm,
                          backgroundColor: formData.role === 'owner' ? theme.colors.light.background.tertiary : theme.colors.white,
                          color: theme.colors.light.text.primary,
                          cursor: formData.role === 'owner' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="view">View Only</option>
                        <option value="edit">View, Edit & Create</option>
                      </select>
                    </div>
                  )}
                </div>
              )}
            </FormGroup>
          </form>
        </Modal>
      </PageContainer>
    </DashboardLayout>
  );
};

export default StaffPage;

